import { c as _c } from "react/compiler-runtime";
import { FontWeights, Tooltip } from "@flat-ui";
import { ThumbnailImage } from "@components/common/thumbnail-image";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { GROUPS_TABLE_COLUMN_LABELS } from "@components/table/groups/groups-table-constants";
import { THUMBNAIL_IMAGE_HEIGHT, THUMBNAIL_IMAGE_WIDTH, withEllipsis } from "@styles/common-styles";
// Here we do not need it as a React component because will be used as src for an image
import defaultGroupThumbnail from "@assets/thumbnails/groups-thumbs/group-thumbnail_default.svg";
import { isGroupDetails, isGroupsTableColumnField } from "@custom-types/type-guards";
import { useDateTime } from "@hooks/use-date-time";
import { getProjectNumbersInGroup } from "@utils/group-utils";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { Stack } from "@mui-wrapper";
import { useMediaQueryList } from "@hooks/use-media-query";

/** Minimum column width in px */
const COLS_MIN_WIDTH_IN_PX = 100;

/** Column width in px for medium or smaller screen */
const MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX = 200;

/** Column width in px for large and larger screens */
const LG_OR_LARGER_COLUMNS_WIDTH_IN_PX = 250;

/** Return all the possible columns for Groups table */
export function useGroupsColumns() {
  const $ = _c(12);
  const {
    isScreenMd,
    isScreenLgOrLarger
  } = useMediaQueryList();
  const {
    formatDate
  } = useDateTime();
  const maxTextColumnWidth = isScreenLgOrLarger ? LG_OR_LARGER_COLUMNS_WIDTH_IN_PX : MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX;
  const flexSetting = isScreenLgOrLarger ? 1 : undefined;
  const {
    columnPreferences,
    visibleOrderedColumns
  } = useColumnPreferencesContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = _temp;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== columnPreferences || $[2] !== flexSetting || $[3] !== formatDate || $[4] !== isScreenMd || $[5] !== maxTextColumnWidth || $[6] !== visibleOrderedColumns) {
    let t2;
    if ($[8] !== formatDate) {
      t2 = params_2 => <var>{formatDate(params_2.row.entity.createdAt)}</var>;
      $[8] = formatDate;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== columnPreferences) {
      t3 = () => <FaroTableColumnVisibilityMenuIcon columnPreferences={columnPreferences} />;
      $[10] = columnPreferences;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    const columnsMap = {
      name: {
        field: "name",
        headerName: GROUPS_TABLE_COLUMN_LABELS.name,
        flex: 1,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        sortable: true,
        disableReorder: true,
        renderCell: t0
      },
      members: {
        field: "members",
        width: isScreenMd ? MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX : LG_OR_LARGER_COLUMNS_WIDTH_IN_PX,
        minWidth: 54,
        sortable: true,
        renderHeader: _temp2,
        renderCell: _temp3
      },
      projects: {
        field: "projects",
        flex: flexSetting,
        width: maxTextColumnWidth,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        sortable: true,
        renderHeader: _temp4,
        renderCell: _temp5
      },
      createdDate: {
        field: "createdDate",
        flex: flexSetting,
        minWidth: COLS_MIN_WIDTH_IN_PX,
        sortable: true,
        renderHeader: _temp6,
        renderCell: t2
      },
      options: {
        field: "options",
        type: "actions",
        align: "right",
        headerName: "",
        width: 90,
        renderHeader: t3,
        disableReorder: true,
        renderCell: _temp7
      }
    };
    t1 = visibleOrderedColumns.filter(isGroupsTableColumnField).map(field => columnsMap[field]);
    $[1] = columnPreferences;
    $[2] = flexSetting;
    $[3] = formatDate;
    $[4] = isScreenMd;
    $[5] = maxTextColumnWidth;
    $[6] = visibleOrderedColumns;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function _temp7() {
  return "";
}
function _temp6() {
  return <TableHeaderWithDraggableIcon title={GROUPS_TABLE_COLUMN_LABELS.createdDate} />;
}
function _temp5(params_1) {
  return <var>{getProjectNumbersInGroup(params_1.row.entity)}</var>;
}
function _temp4() {
  return <TableHeaderWithDraggableIcon title={GROUPS_TABLE_COLUMN_LABELS.projects} />;
}
function _temp3(params_0) {
  if (!isGroupDetails(params_0.row.entity)) {
    return <var>{params_0.row.entity.numManagers}</var>;
  }
}
function _temp2() {
  return <TableHeaderWithDraggableIcon title={GROUPS_TABLE_COLUMN_LABELS.members} />;
}
function _temp(params) {
  return <Stack direction="row" sx={{
    ...withEllipsis,
    alignItems: "center",
    gap: "0.625rem"
  }}><ThumbnailImage src={params.row.entity.thumbnailUrl || undefined} fallbackSrc={defaultGroupThumbnail} alt="Group thumbnail" sx={{
      height: `${THUMBNAIL_IMAGE_HEIGHT}px`,
      width: `${THUMBNAIL_IMAGE_WIDTH}px`,
      objectFit: "cover"
    }} /><Tooltip component="div" title={<var>{params.row.entity.name}</var>} sx={{
      ...withEllipsis,
      fontWeight: FontWeights.Bold
    }}><var>{params.row.entity.name}</var></Tooltip></Stack>;
}